import { ref } from 'vue';
import { getHostRouter } from '@Src/mfe.config';
import { prepareTaskNavigationApi, startTaskApi } from '@Api/dashboard.api';
import { useDashboardStore } from '@Stores/dashboard.store';
import { useTaskPriming } from '@Use/useTaskPriming';
import { useTaskSheet } from '@Use/useTaskSheet';
import { isMatchTask } from '@Src/shared/utils/resolveEffectiveType';
import type { TaskItem } from '@Types/dashboard.types';

// Task types that are not yet navigable
const UNSUPPORTED_TASK_TYPES = new Set<string>([]);

// Task types that require a company slug prefix in the path
const SLUG_PREFIXED_MFES = new Set([
    'document_signing',
    'payment_emd',
    'payment_c2c',
    'payment_request',
    'payment_repayment',
    'complete_match_request',
    'match',
    'send',
    'collect',
    'confirm',
    'complete_scc_request',
]);

// Completed task types that should NOT be navigable. Historically this
// included 'complete_match_request' because re-navigating to a completed
// IDV flow had no purpose. Now that completed tasks with a registered
// completion view open the view-only sheet instead, the set can be
// empty — kept around as the seam for future cases where a completed
// task truly has nowhere to go.
const NON_NAVIGABLE_WHEN_COMPLETED = new Set<string>([]);

export function useTaskNavigation() {
    const hostRouter = getHostRouter();
    const isNavigating = ref(false);
    const navigationError = ref<string | null>(null);
    const { taskHasPriming, openSheet } = useTaskPriming();
    const { taskHasView, openTaskSheet } = useTaskSheet();

    const isTaskNavigable = (task: TaskItem): boolean => {
        if (!task.taskType) return false;
        if (UNSUPPORTED_TASK_TYPES.has(task.taskType)) return false;
        if (task.status === 'completed' && NON_NAVIGABLE_WHEN_COMPLETED.has(task.taskType)) return false;
        if (task.isLocked === true) return false;
        return true;
    };

    // Apply the company slug prefix to a BFF-supplied path when the task type
    // requires it. SLUG_PREFIXED_MFES is the single source of truth used by
    // both the happy path and the locker-redirect path.
    const buildFinalPath = (path: string, taskType: string): string => {
        if (!hostRouter || !SLUG_PREFIXED_MFES.has(taskType)) return path;
        const slug = hostRouter.currentRoute.value.params.companySlug as string;
        return slug ? `/${slug}${path}` : path;
    };

    const executeNavigation = async (task: TaskItem) => {
        if (!hostRouter) return;

        isNavigating.value = true;
        navigationError.value = null;

        try {
            const nav = await prepareTaskNavigationApi(task.id, task.dealId);

            // BFF signals that this task is locked by a different task (e.g. Match).
            // Retry once against the locker task to redirect the user there instead.
            if (nav?.errorCode === 'TASK_LOCKED_BY_MATCH' && nav.lockedByTaskId) {
                const retryNav = await prepareTaskNavigationApi(nav.lockedByTaskId, task.dealId);
                if (retryNav?.path) {
                    // Locker is always the participant's Match task in V1; explicit
                    // taskType makes the slug-prefix decision symmetric with happy path.
                    hostRouter.push({
                        path: buildFinalPath(retryNav.path, 'complete_match_request'),
                        query: { ...retryNav.queryParams, source: 'dashboard' },
                    });
                } else {
                    // DS-2893 review @certifid-pr-buddy: defensive surface for a future regression
                    // where the retry-against-locker also fails to produce a path (e.g. the locker
                    // chain-locks for some reason, or its TaskData becomes incomplete). Today
                    // unreachable — the Match task has no LockedByTaskId so it always falls
                    // through to a real navigation path — but silently returning gave the user
                    // zero feedback on click. Now they see a recoverable error instead.
                    navigationError.value = 'Unable to open this task. Please try again.';
                }
                return;
            }

            if (nav?.path) {
                hostRouter.push({
                    path: buildFinalPath(nav.path, task.taskType ?? ''),
                    query: { ...nav.queryParams, source: 'dashboard' },
                });
            }
        } catch (error) {
            console.error('[Dashboard MFE] Task navigation failed:', error);
            navigationError.value = 'Unable to open this task. Please try again.';
        } finally {
            isNavigating.value = false;
        }
    };

    const navigateToTask = async (task: TaskItem) => {
        if (!task.taskType || !isTaskNavigable(task)) return;

        // DS-2893: a failed / unable-to-verify Match task arrives with status="completed"
        // (the DealTask workflow completed; the IDV outcome was non-verified). The completion
        // sheet for match / complete_match_request renders the verified-success view, which
        // is wrong for a non-verified outcome. Skip the completion sheet so the retry CTA
        // falls through to executeNavigation and re-enters the IDV flow.
        const isMatchRetry = isMatchTask(task)
            && (task.matchOutcome === 'failed' || task.matchOutcome === 'unable_to_verify');

        // Completed tasks open a view-only sheet when a view is registered
        // for their type (see taskViews.ts). Unregistered completed types
        // fall through to the existing navigation path so missing views are
        // a visible gap, not a silent generic screen.
        if (task.status === 'completed' && taskHasView(task) && !isMatchRetry) {
            openTaskSheet(task);
            return;
        }

        // Fraud education opens the unified TaskSheet for active + completed
        // (DS-2860). No priming screen — the sheet IS the task surface.
        if (task.taskType === 'fraud_education') {
            if (task.status === 'pending') {
                startTaskApi(task.id)
                    .then(() => useDashboardStore().loadDashboard())
                    .catch(() => { /* non-blocking */ });
            }
            openTaskSheet(task);
            return;
        }

        // Task types with a priming config open the priming sheet first; the
        // sheet's Continue CTA calls executeNavigation(task) to preserve the
        // existing URL logic. Completed tasks skip priming and re-enter the
        // flow directly. Task types without a config fall through.
        if (task.status !== 'completed' && taskHasPriming(task)) {
            openSheet(task);
            return;
        }

        // Mirror openSheet's pending → in_progress flip for the no-priming
        // path, so the dashboard card reflects engagement on reload even
        // when there's no priming gate (e.g. match / complete_match_request).
        if (task.status === 'pending') {
            startTaskApi(task.id)
                .then(() => useDashboardStore().loadDashboard())
                .catch(() => { /* non-blocking */ });
        }

        await executeNavigation(task);
    };

    return {
        navigateToTask,
        executeNavigation,
        isNavigating,
        navigationError,
        isTaskNavigable,
    };
}
